/**
 * Arabic RTL Auto-Apply Script for Shopify
 * 아랍어 콘텐츠 자동 RTL 적용 스크립트
 * 
 * 기능:
 * - 페이지의 모든 텍스트 요소를 스캔
 * - 아랍어 문자가 포함된 요소에 자동으로 dir="rtl" 적용
 * - 유니코드 BiDi 알고리즘으로 영어/숫자 자동 처리
 * - 써드파티 앱 영역 자동 제외
 */

/**
 * Version 2. (2025.12.02)
 * 아랍어 콘텐츠 자동 RTL 적용 스크립트 (스마트 정렬)
 * * 변경점:
 * - 기본적으로 RTL 적용 후 '왼쪽 정렬'을 강제함 (기존 동일)
 * - 단, 원래 스타일이 '중앙 정렬(Center)'인 경우 이를 존중하여 중앙 정렬 유지
 */

/**
 * Version 3. (2026.08.24)
 * 변경점:
 * - 오켄도(Okendo) 리뷰는 나중에 그려져서 기존 1회 실행으로는 못 잡았음 → MutationObserver 추가
 * - 단, 감시 재실행은 '리뷰 제목/본문'에만 적용한다.
 *   일반 선택자(p, li, span...)로 재실행하면 리뷰를 감싼 컨테이너까지 dir=rtl이 붙어
 *   프로필 카드·날짜·버튼 위치가 통째로 좌우 반전된다.
 * - 정렬 규칙은 기존 그대로 유지 (RTL + 왼쪽 정렬, 원래 중앙이면 중앙 유지)
 */

(function() {
  'use strict';
  
  // 아랍어 문자 감지 정규식
  const ARABIC_REGEX = /[\u0600-\u06FF]/;
  
  function hasArabicCharacters(text) {
    return text && ARABIC_REGEX.test(text);
  }
  
  const TEXT_SELECTORS = [
    'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'li', 'td', 'th', 'span:not([class*="icon"])',
    '.product__title',
    '.product__description p',
    '.rte p'
  ].join(', ');
  
  const EXCLUDE_CLASSES = ['no-rtl', 'ltr-only'];
  
  function isInsideThirdPartyApp(element) {
    return element.closest('[data-shopify-app-block]') !== null;
  }
  
  function applyRTLToArabicContent() {
    const elements = document.querySelectorAll(TEXT_SELECTORS);
    
    elements.forEach(element => {
      if (isInsideThirdPartyApp(element)) return;
      if (element.hasAttribute('dir')) return;
      if (EXCLUDE_CLASSES.some(cls => element.classList.contains(cls))) return;
      
      const textContent = element.textContent || '';
      
      if (hasArabicCharacters(textContent)) {
        // 1. 현재 요소의 계산된 스타일(실제 적용된 스타일)을 확인
        const currentStyle = window.getComputedStyle(element);
        const isCentered = currentStyle.textAlign === 'center';
        
        // 2. RTL 속성 적용 (글자 순서는 뒤집힘)
        element.setAttribute('dir', 'rtl');
        
        // 3. 정렬 로직 분기
        if (isCentered) {
          // 원래 중앙 정렬이었다면 -> 중앙 정렬 강제 유지
          element.style.setProperty('text-align', 'center', 'important');
        } else {
          // 중앙 정렬이 아니었다면 -> 왼쪽 정렬 강제 (RTL의 기본인 오른쪽 정렬 방지)
          element.style.setProperty('text-align', 'left', 'important');
        }
      }
    });
  }
  
  // 오켄도 리뷰에서 '글자 자체'가 들어있는 요소만 콕 집는다.
  // 컨테이너를 건드리면 카드 레이아웃이 뒤집히므로 절대 넓히지 말 것.
  const OKENDO_TEXT_SELECTORS = [
    '.oke-reviewContent-title',
    '.oke-reviewContent-body p'
  ].join(', ');
  
  function applyRTLToOkendoReviews() {
    document.querySelectorAll(OKENDO_TEXT_SELECTORS).forEach(element => {
      if (element.hasAttribute('dir')) return;
      if (EXCLUDE_CLASSES.some(cls => element.classList.contains(cls))) return;
      if (!hasArabicCharacters(element.textContent || '')) return;
      
      const isCentered = window.getComputedStyle(element).textAlign === 'center';
      element.setAttribute('dir', 'rtl');
      element.style.setProperty('text-align', isCentered ? 'center' : 'left', 'important');
    });
  }
  
  // 오켄도 리뷰가 나중에 그려지는 것을 감시한다.
  // 우리가 dir/style을 바꾸는 것도 변경으로 잡히므로,
  // 처리하는 동안에는 관찰을 끊었다가 다시 잇는다.
  function watchForLateContent() {
    if (!('MutationObserver' in window)) return;
    
    let timer = null;
    const observer = new MutationObserver(() => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        observer.disconnect();
        applyRTLToOkendoReviews();
        observer.observe(document.body, { childList: true, subtree: true });
      }, 200);
    });
    
    observer.observe(document.body, { childList: true, subtree: true });
  }
  
  function initialize() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        applyRTLToArabicContent();
        watchForLateContent();
      });
    } else {
      applyRTLToArabicContent();
      watchForLateContent();
    }
  }
  
  initialize();
  
})();
